import { useState, useEffect, useRef } from 'react';
import { auth } from '../firebase/config';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useAuthContext } from '../hooks/useAuthContext';

export const useLogin = () => {
  const [error, setError] = useState(null);
  const [isPending, setIsPending] = useState(false);
  const { dispatch } = useAuthContext();
  
  // Use a ref to track component mounted state
  const isMounted = useRef(true);

  const login = async (email, password) => {
    // Reset states at the start of login attempt
    if (!isMounted.current) return { success: false, error: 'Login process interrupted' };

    setError(null);
    setIsPending(true);

    try {
      const res = await signInWithEmailAndPassword(auth, email, password);
      
      // Check if component is still mounted before updating state
      if (isMounted.current) {
        dispatch({ type: 'LOGIN', payload: res.user });
        setIsPending(false);
        return { success: true };
      }
      
      return { success: false, error: 'Login process interrupted' };
    } catch (err) {
      // Only update state if component is still mounted
      if (isMounted.current) {
        setIsPending(false);
        
        // Handle specific Firebase error codes
        let errorMessage;
        switch (err.code) {
          case 'auth/user-not-found':
            errorMessage = 'No account found with this email';
            break;
          case 'auth/wrong-password':
            errorMessage = 'Incorrect password';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Invalid email address';
            break;
          case 'auth/too-many-requests':
            errorMessage = 'Too many failed login attempts. Please try again later';
            break;
          default:
            errorMessage = 'Failed to login. Please check your credentials';
        }
        
        // Log the full error for debugging
        console.error('Login Error:', err);
        
        // Set the error state
        setError(errorMessage);
        
        return { 
          success: false, 
          error: errorMessage 
        };
      }
      
      // Fallback return if component was unmounted
      return { 
        success: false, 
        error: 'Login process interrupted' 
      };
    }
  };

  useEffect(() => {
    // Reset isMounted ref to true when component mounts
    isMounted.current = true;
    
    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted.current = false;
    };
  }, []);

  return { login, error, isPending };
};